import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './SocialLinks.styled';
import { EmailShare, FacebookShare, LinkedinShare, SmartphoneShare, XShare, CheckCircleFilled, InstagramShare } from '../../svgs';
import { useTimeout } from '../../hooks/useTimeout';
import { StyledContentContainer } from '../../containers/StyledContentContainer.styled';
import { setColorWithOpacityCss } from '../../utils/setColorWithOpacityCss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DEFAULT_APPEAL_SET_CONFIGURATION = {
  copy_link: {
    enabled: true
  },
  email: {
    enabled: true
  },
  facebook: {
    enabled: true
  },
  linkedin: {
    enabled: false
  },
  text_message: {
    enabled: true
  },
  twitter: {
    enabled: true
  }
};
const socialLinksDataIds = {
  copyLink: 'cp-social-links-copy-link',
  email: 'cp-social-links-email',
  facebook: 'cp-social-links-facebook',
  instagram: 'cp-social-links-instagram',
  linkedin: 'cp-social-links-linkedin',
  text: 'cp-social-links-sms',
  twitter: 'cp-social-links-twitter'
};

/**
 * The `SocialLinks` content block presents a list of buttons for sharing a campaign on social media
 * platforms.
 */
export const SocialLinks = _ref => {
  let {
    alignXPreset,
    appealSet = DEFAULT_APPEAL_SET_CONFIGURATION,
    cssColor,
    cssBackgroundColorOpacity,
    cssBackgroundColor,
    cssBorderColor,
    cssBorderWidth,
    cssBorderRadius,
    dataIds = socialLinksDataIds,
    isInstagramSharingEnabled = false,
    isMobileOrTablet = false,
    openLinksInNewTab = true,
    parentLayoutInfo,
    socialShareLinks,
    ...rest
  } = _ref;
  const [linkIsCopied, setLinkIsCopied] = useState(false);
  const {
    t
  } = useTranslation('blocks.socialLinks');
  const onCopyLink = useCallback(() => {
    if (typeof navigator !== undefined && socialShareLinks?.copy) {
      setLinkIsCopied(true);
      navigator.clipboard.writeText(socialShareLinks?.copy);
    }
  }, [socialShareLinks?.copy]);
  useTimeout(() => {
    setLinkIsCopied(false);
  }, linkIsCopied ? 7000 : null);
  return /*#__PURE__*/_jsx(StyledContentContainer, {
    parentLayoutInfo: parentLayoutInfo,
    children: /*#__PURE__*/_jsxs(Styled.SocialLinksContainer, {
      "data-id": "cp-social-links",
      alignXPreset: alignXPreset,
      ...rest,
      children: [appealSet.facebook.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.facebook,
        "data-id": dataIds.facebook,
        target: openLinksInNewTab ? '_blank' : undefined,
        children: [/*#__PURE__*/_jsx(FacebookShare, {}), " ", t('SocialLinks.facebook', 'Facebook')]
      }), appealSet.linkedin.enabled && false && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.linkedin,
        "data-id": dataIds.linkedin,
        target: openLinksInNewTab ? '_blank' : undefined,
        children: [/*#__PURE__*/_jsx(LinkedinShare, {}), " ", t('SocialLinks.linkedin', 'LinkedIn')]
      }), appealSet.twitter.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.twitter,
        "data-id": dataIds.twitter,
        target: openLinksInNewTab ? '_blank' : undefined,
        children: [/*#__PURE__*/_jsx(XShare, {}), " ", t('SocialLinks.post', 'Post')]
      }), isInstagramSharingEnabled && isMobileOrTablet && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.instagram,
        "data-id": dataIds.instagram,
        target: openLinksInNewTab ? '_blank' : undefined,
        children: [/*#__PURE__*/_jsx(InstagramShare, {}), " ", t('SocialLinks.instagram', 'Instagram')]
      }), appealSet.text_message.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.sms,
        "data-id": dataIds.text,
        children: [/*#__PURE__*/_jsx(SmartphoneShare, {}), " ", t('SocialLinks.text', 'Text')]
      }), appealSet.email.enabled && /*#__PURE__*/_jsxs(Styled.SocialLinkButton, {
        cssColor: cssColor,
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        cssBorderWidth: cssBorderWidth,
        cssBorderColor: cssBorderColor,
        cssBorderRadius: cssBorderRadius,
        href: socialShareLinks?.email,
        "data-id": dataIds.email,
        target: "_blank",
        children: [/*#__PURE__*/_jsx(EmailShare, {}), " ", t('SocialLinks.email', 'Email')]
      }), appealSet.copy_link.enabled && /*#__PURE__*/_jsxs(Styled.CopyLinkContainer, {
        children: [/*#__PURE__*/_jsx(Styled.CopyLinkButton, {
          cssColor: cssColor,
          cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
          cssBorderWidth: cssBorderWidth,
          cssBorderColor: cssBorderColor,
          cssBorderRadius: cssBorderRadius,
          onClick: onCopyLink,
          onKeyDown: e => (e.key === 'Enter' || e.key === ' ') && onCopyLink(),
          "data-id": dataIds.copyLink,
          role: "button",
          tabIndex: 0,
          children: linkIsCopied ? /*#__PURE__*/_jsxs(Styled.CopyLinkButtonText, {
            "aria-label": t('SocialLinks.copyLinkAriaStatus', 'Campaign link status'),
            "aria-live": "assertive",
            children: [t('SocialLinks.linkCopied', 'Copied'), /*#__PURE__*/_jsx(CheckCircleFilled, {})]
          }) : /*#__PURE__*/_jsx("span", {
            children: t('SocialLinks.copyLink', 'Copy link')
          })
        }), /*#__PURE__*/_jsx(Styled.CopyLinkInputContainer, {
          onClick: onCopyLink,
          children: /*#__PURE__*/_jsx(Styled.CopyLinkInput, {
            parentLayoutInfo: parentLayoutInfo,
            cssBorderRadius: cssBorderRadius,
            cssBorderWidth: cssBorderWidth,
            cssBorderColor: cssBorderColor,
            id: "copy-link",
            "aria-label": t('SocialLinks.copyCampaignLinkAriaLabel', 'Copy campaign link'),
            defaultValue: socialShareLinks?.copy,
            readOnly: true
          })
        })]
      })]
    })
  });
};